import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ArrowLeft,
  Search,
  Mail,
  RefreshCw,
  Filter,
  Send,
  CheckCircle2,
  AlertTriangle,
  Clock,
  HelpCircle,
  Archive,
  Trash2,
  ShieldAlert,
  StickyNote,
} from "lucide-react";
import { requireAdmin } from "@/lib/admin-guard";
import {
  listSupportRequests,
  updateSupportRequest,
  resendSupportEmail,
  listSupportRequestNotes,
  addSupportRequestNote,
  archiveSupportRequest,
  deleteSupportRequestPermanently,
  type SupportRequestRow,
  type EmailDeliveryStatus,
  type SupportRequestStatus,
  type SupportAccountType,
} from "@/lib/support-admin.functions";

export const Route = createFileRoute("/admin_/support-inbox")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Support Inbox — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: AdminSupportInboxPage,
});

type StatusFilter = "all" | SupportRequestStatus;

const STATUS_LABEL: Record<SupportRequestStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  archived: "Archived",
};

const STATUS_STYLES: Record<SupportRequestStatus, string> = {
  open: "bg-amber-100 text-amber-900 border-amber-200 dark:bg-amber-950/70 dark:text-amber-200 dark:border-amber-700/60",
  in_progress: "bg-blue-100 text-blue-900 border-blue-200 dark:bg-blue-950/70 dark:text-blue-200 dark:border-blue-700/60",
  resolved: "bg-emerald-100 text-emerald-900 border-emerald-200 dark:bg-emerald-950/70 dark:text-emerald-200 dark:border-emerald-700/60",
  archived: "bg-muted text-muted-foreground border-border",
};

const ACCOUNT_LABEL: Record<SupportAccountType, string> = {
  administrator: "Administrator",
  founding_member: "Founding Member",
  therapist: "Therapist",
  guest_member: "Free Account (Payment Required)",
  guest_no_account: "Guest / No account",
};

const ACCOUNT_STYLES: Record<SupportAccountType, string> = {
  administrator: "bg-purple-100 text-purple-900 border-purple-200 dark:bg-purple-950/70 dark:text-purple-200 dark:border-purple-700/60",
  founding_member: "bg-gold/15 text-[var(--gold-deep)] border-gold/40",
  therapist: "bg-teal-100 text-teal-900 border-teal-200 dark:bg-teal-950/70 dark:text-teal-200 dark:border-teal-700/60",
  guest_member: "bg-muted text-foreground border-border",
  guest_no_account: "bg-muted text-muted-foreground border-border",
};

const DELIVERY_META: Record<
  EmailDeliveryStatus,
  { label: string; className: string; Icon: typeof CheckCircle2 }
> = {
  sent: {
    label: "Delivered",
    className: "bg-emerald-100 text-emerald-900 border-emerald-200 dark:bg-emerald-950/70 dark:text-emerald-200 dark:border-emerald-700/60",
    Icon: CheckCircle2,
  },
  pending: {
    label: "Queued",
    className: "bg-blue-100 text-blue-900 border-blue-200 dark:bg-blue-950/70 dark:text-blue-200 dark:border-blue-700/60",
    Icon: Clock,
  },
  dlq: {
    label: "Failed",
    className: "bg-red-100 text-red-900 border-red-200 dark:bg-red-950/70 dark:text-red-200 dark:border-red-700/60",
    Icon: AlertTriangle,
  },
  failed: {
    label: "Failed",
    className: "bg-red-100 text-red-900 border-red-200 dark:bg-red-950/70 dark:text-red-200 dark:border-red-700/60",
    Icon: AlertTriangle,
  },
  bounced: {
    label: "Bounced",
    className: "bg-red-100 text-red-900 border-red-200 dark:bg-red-950/70 dark:text-red-200 dark:border-red-700/60",
    Icon: AlertTriangle,
  },
  complained: {
    label: "Complaint",
    className: "bg-red-100 text-red-900 border-red-200 dark:bg-red-950/70 dark:text-red-200 dark:border-red-700/60",
    Icon: AlertTriangle,
  },
  suppressed: {
    label: "Suppressed",
    className: "bg-amber-100 text-amber-900 border-amber-200 dark:bg-amber-950/70 dark:text-amber-200 dark:border-amber-700/60",
    Icon: AlertTriangle,
  },
  unknown: {
    label: "No log",
    className: "bg-muted text-muted-foreground border-border",
    Icon: HelpCircle,
  },
};

function AdminSupportInboxPage() {
  const qc = useQueryClient();
  const list = useServerFn(listSupportRequests);
  const update = useServerFn(updateSupportRequest);
  const resend = useServerFn(resendSupportEmail);
  const listNotes = useServerFn(listSupportRequestNotes);
  const addNote = useServerFn(addSupportRequestNote);
  const archive = useServerFn(archiveSupportRequest);
  const permaDelete = useServerFn(deleteSupportRequestPermanently);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("open");
  const [topic, setTopic] = useState<string>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const key = ["admin", "support-inbox", status, topic, search] as const;
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: key,
    queryFn: () =>
      list({
        data: {
          search: search.trim() || undefined,
          topic: topic === "all" ? undefined : topic,
          status: status === "all" ? undefined : status,
        },
      }),
  });

  const rows = data?.rows ?? [];
  const topics = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => s.add(r.topic));
    return Array.from(s).sort();
  }, [rows]);

  const stats = useMemo(() => {
    return {
      total: rows.length,
      open: rows.filter((r) => r.status === "open").length,
      in_progress: rows.filter((r) => r.status === "in_progress").length,
      resolved: rows.filter((r) => r.status === "resolved").length,
      archived: rows.filter((r) => r.status === "archived").length,
    };
  }, [rows]);

  const selected = rows.find((r) => r.id === selectedId) ?? null;

  const notesQuery = useQuery({
    queryKey: ["admin", "support-inbox", "notes", selectedId],
    queryFn: () =>
      selectedId
        ? listNotes({ data: { request_id: selectedId } })
        : Promise.resolve({ notes: [] }),
    enabled: !!selectedId,
  });

  useEffect(() => {
    setNoteDraft("");
    setDeleteOpen(false);
    setDeleteConfirm("");
    setDeleteReason("");
  }, [selectedId]);

  async function setRowStatus(id: string, next: SupportRequestStatus) {
    try {
      await update({ data: { id, status: next } });
      toast.success(`Marked ${STATUS_LABEL[next].toLowerCase()}`);
      await qc.invalidateQueries({ queryKey: ["admin", "support-inbox"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Update failed");
    }
  }

  async function handleArchive(id: string) {
    try {
      await archive({ data: { id } });
      toast.success("Request archived");
      await qc.invalidateQueries({ queryKey: ["admin", "support-inbox"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Archive failed");
    }
  }

  async function handleAddNote() {
    if (!selectedId || !noteDraft.trim()) return;
    try {
      await addNote({ data: { request_id: selectedId, note: noteDraft.trim() } });
      setNoteDraft("");
      toast.success("Internal note added");
      await qc.invalidateQueries({
        queryKey: ["admin", "support-inbox", "notes", selectedId],
      });
      await qc.invalidateQueries({ queryKey: ["admin", "support-inbox"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not add note");
    }
  }

  async function handleResend(id: string) {
    if (resendingId) return;
    setResendingId(id);
    try {
      const r = await resend({ data: { id } });
      toast.success("Notification queued for resend", {
        description: `Retrying delivery to ${r.routed_to}. Refresh in a moment to see the new status.`,
      });
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["admin", "support-inbox"] });
      }, 3000);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Resend failed");
    } finally {
      setResendingId(null);
    }
  }

  async function handlePermanentDelete() {
    if (!selectedId) return;
    if (deleteConfirm !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    if (deleteReason.trim().length < 3) {
      toast.error("Please provide a short reason for the audit log");
      return;
    }
    setDeleting(true);
    try {
      await permaDelete({
        data: {
          id: selectedId,
          confirmation: "DELETE",
          reason: deleteReason.trim(),
        },
      });
      toast.success("Support request permanently deleted", {
        description: "Recorded in the audit log.",
      });
      setSelectedId(null);
      setDeleteOpen(false);
      await qc.invalidateQueries({ queryKey: ["admin", "support-inbox"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <Link
            to="/dashboard"
            className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </Link>
          <h1 className="font-serif text-3xl text-foreground">Support Inbox</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Messages sent from the public Support form. Requests are always stored,
            even if the notification email fails.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
        {[
          { label: "Total in view", value: stats.total },
          { label: "Open", value: stats.open },
          { label: "In progress", value: stats.in_progress },
          { label: "Resolved", value: stats.resolved },
          { label: "Archived", value: stats.archived },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-border bg-card p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {s.label}
            </div>
            <div className="mt-1 text-2xl font-semibold">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email, name, topic, message…"
            className="w-full rounded-full border border-border bg-background py-2 ps-9 pe-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          className="rounded-full border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="resolved">Resolved</option>
          <option value="archived">Archived</option>
          <option value="all">All (incl. archived)</option>
        </select>
        <select
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          className="rounded-full border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="all">All topics</option>
          {topics.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load"}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        {/* List */}
        <div className="rounded-2xl border border-border bg-card">
          {isLoading ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              <Filter className="mx-auto mb-2 h-6 w-6 opacity-50" />
              No support requests match these filters.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {rows.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                    className={`flex w-full flex-col gap-1 p-4 text-start transition-colors hover:bg-accent/40 ${
                      selectedId === r.id ? "bg-accent/60" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-foreground">
                        {r.name || r.email}
                      </span>
                      <div className="flex shrink-0 items-center gap-1">
                        {(() => {
                          const dm = DELIVERY_META[r.delivery.status];
                          const Icon = dm.Icon;
                          return (
                            <span
                              className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${dm.className}`}
                              title={r.delivery.error_message ?? undefined}
                            >
                              <Icon className="h-3 w-3" /> {dm.label}
                            </span>
                          );
                        })()}
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${STATUS_STYLES[r.status]}`}
                        >
                          {STATUS_LABEL[r.status]}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5 truncate">
                        <span
                          className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${ACCOUNT_STYLES[r.account_type]}`}
                        >
                          {ACCOUNT_LABEL[r.account_type]}
                        </span>
                        <span className="truncate">
                          {r.topic} → {r.routed_to}
                        </span>
                      </span>
                      <span className="shrink-0">
                        {new Date(r.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="line-clamp-2 text-xs text-foreground/80">{r.message}</p>
                    {r.note_count > 0 && (
                      <span className="mt-1 inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                        <StickyNote className="h-3 w-3" /> {r.note_count} internal note
                        {r.note_count === 1 ? "" : "s"}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <aside className="rounded-2xl border border-border bg-card p-5">
          {!selected ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Select a message to view details.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${ACCOUNT_STYLES[selected.account_type]}`}
                >
                  {ACCOUNT_LABEL[selected.account_type]}
                </span>
                <span
                  className={`rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide ${STATUS_STYLES[selected.status]}`}
                >
                  {STATUS_LABEL[selected.status]}
                </span>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Topic
                </div>
                <div className="mt-0.5 text-sm font-medium">{selected.topic}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  From
                </div>
                <div className="mt-0.5 text-sm">
                  {selected.name ? `${selected.name} · ` : ""}
                  <a
                    href={`mailto:${selected.email}`}
                    className="text-gold hover:underline"
                  >
                    {selected.email}
                  </a>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Received
                </div>
                <div className="mt-0.5 text-sm">
                  {new Date(selected.created_at).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Routed to
                </div>
                <div className="mt-0.5 text-sm">{selected.routed_to}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Message
                </div>
                <p className="mt-1 whitespace-pre-wrap rounded-xl border border-border bg-background p-3 text-sm">
                  {selected.message}
                </p>
              </div>

              {/* Delivery status panel */}
              {(() => {
                const dm = DELIVERY_META[selected.delivery.status];
                const Icon = dm.Icon;
                const canResend = selected.delivery.status !== "pending";
                return (
                  <div className="rounded-xl border border-border bg-background p-3">
                    <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                      Notification email delivery
                    </div>
                    <div className="flex items-start gap-2 text-sm">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide ${dm.className}`}
                      >
                        <Icon className="h-3 w-3" /> {dm.label}
                      </span>
                      <div className="flex-1 text-xs text-muted-foreground">
                        <div>
                          To: <span className="text-foreground">{selected.routed_to}</span>
                        </div>
                        {selected.delivery.last_attempt_at && (
                          <div>
                            Last attempt:{" "}
                            {new Date(selected.delivery.last_attempt_at).toLocaleString()}
                          </div>
                        )}
                        <div className="mt-1 font-mono text-[10px] break-all">
                          {selected.delivery.message_id}
                        </div>
                      </div>
                    </div>
                    {selected.delivery.error_message && (
                      <p className="mt-2 rounded-md bg-destructive/5 p-2 text-xs text-destructive">
                        {selected.delivery.error_message}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => handleResend(selected.id)}
                      disabled={!canResend || resendingId === selected.id}
                      className="mt-3 inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
                    >
                      <Send
                        className={`h-3.5 w-3.5 ${resendingId === selected.id ? "animate-pulse" : ""}`}
                      />
                      {resendingId === selected.id ? "Resending…" : "Resend notification"}
                    </button>
                  </div>
                );
              })()}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2">
                <a
                  href={`mailto:${selected.email}?subject=${encodeURIComponent(
                    `Re: [${selected.topic}] Support request`,
                  )}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-gold px-4 py-2 text-xs font-semibold text-white hover:bg-[var(--gold-deep)]"
                >
                  <Mail className="h-3.5 w-3.5" /> Reply
                </a>
                {(["open", "in_progress", "resolved"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setRowStatus(selected.id, s)}
                    disabled={selected.status === s}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${STATUS_STYLES[s]}`}
                  >
                    {STATUS_LABEL[s]}
                  </button>
                ))}
                {selected.status !== "archived" ? (
                  <button
                    type="button"
                    onClick={() => handleArchive(selected.id)}
                    className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
                  >
                    <Archive className="h-3.5 w-3.5" /> Archive
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setRowStatus(selected.id, "resolved")}
                    className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
                  >
                    Unarchive → Resolved
                  </button>
                )}
              </div>

              {/* Internal notes */}
              <div className="rounded-xl border border-border bg-background p-3">
                <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                  <StickyNote className="h-3.5 w-3.5" /> Internal notes
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] normal-case text-muted-foreground">
                    Admins only — never sent to the customer
                  </span>
                </div>
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Add a private note…"
                  rows={3}
                  className="w-full rounded-lg border border-border bg-background p-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={handleAddNote}
                    disabled={!noteDraft.trim()}
                    className="rounded-full bg-foreground px-3 py-1.5 text-xs font-medium text-background hover:opacity-90 disabled:opacity-50"
                  >
                    Add note
                  </button>
                </div>

                <ul className="mt-3 space-y-2">
                  {(notesQuery.data?.notes ?? []).length === 0 ? (
                    <li className="text-xs text-muted-foreground">No notes yet.</li>
                  ) : (
                    (notesQuery.data?.notes ?? []).map((n) => (
                      <li
                        key={n.id}
                        className="rounded-lg border border-border bg-card p-2 text-xs"
                      >
                        <div className="mb-1 flex items-center justify-between text-[10px] text-muted-foreground">
                          <span>{n.author_email ?? "Admin"}</span>
                          <span>{new Date(n.created_at).toLocaleString()}</span>
                        </div>
                        <p className="whitespace-pre-wrap text-foreground">{n.note}</p>
                      </li>
                    ))
                  )}
                </ul>
              </div>

              {/* Permanent delete (guarded) */}
              <details
                className="rounded-xl border border-destructive/30 bg-destructive/5 p-3"
                open={deleteOpen}
                onToggle={(e) => setDeleteOpen((e.target as HTMLDetailsElement).open)}
              >
                <summary className="flex cursor-pointer items-center gap-2 text-xs font-medium text-destructive">
                  <ShieldAlert className="h-4 w-4" /> Permanent delete (admin only)
                </summary>
                <div className="mt-3 space-y-2 text-xs text-destructive/90">
                  <p>
                    This permanently removes the request and any internal notes.
                    Use only for privacy / data-retention requests. An audit-log entry
                    is written with your admin ID and the reason below. Prefer{" "}
                    <strong>Archive</strong> to keep the record searchable.
                  </p>
                  <input
                    type="text"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Reason (required, kept in audit log)"
                    className="w-full rounded-md border border-destructive/40 bg-background p-2 text-xs text-foreground"
                  />
                  <input
                    type="text"
                    value={deleteConfirm}
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    placeholder="Type DELETE to confirm"
                    className="w-full rounded-md border border-destructive/40 bg-background p-2 text-xs text-foreground"
                  />
                  <button
                    type="button"
                    onClick={handlePermanentDelete}
                    disabled={
                      deleting ||
                      deleteConfirm !== "DELETE" ||
                      deleteReason.trim().length < 3
                    }
                    className="inline-flex items-center gap-1.5 rounded-full bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {deleting ? "Deleting…" : "Permanently delete"}
                  </button>
                </div>
              </details>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
